// src/pages/system/RouterSyncStatus.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Sync as SyncIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorIcon,
  CheckCircleOutline as SuccessIcon,
  HourglassEmpty as PendingIcon,
  PlayCircleOutline as ProcessingIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

const RouterSyncStatus = () => {
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTask, setActiveTask] = useState(null);

  // Dialogs
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  // Notifications
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const fetchQueueData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const [listRes, summaryRes] = await Promise.all([
        fetch('/api/routers/sync-queue/list', { headers }),
        fetch('/api/routers/sync-queue/summary', { headers })
      ]);

      if (!listRes.ok || !summaryRes.ok) {
        throw new Error('Error al obtener datos de la cola');
      }

      const listData = await listRes.json();
      const summaryData = await summaryRes.json();

      setTasks(listData);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error fetching queue data:', error);
      showNotification(error.message || 'Error al cargar la cola de sincronización', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueueData();
    // Auto-refresh summary and list every 30 seconds
    const interval = setInterval(fetchQueueData, 30000);
    return () => clearInterval(interval);
  }, [fetchQueueData]);

  const handleRetryTask = async (id) => {
    setActionLoading(true);
    setActiveTask(id);
    try {
      const response = await fetch(`/api/routers/sync-queue/${id}/retry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al reintentar la tarea');
      }

      if (data.success) {
        showNotification('Sincronización manual completada con éxito en el MikroTik', 'success');
      } else {
        showNotification('El reintento falló: ' + (data.task?.last_error || 'Error de conexión'), 'warning');
      }

      fetchQueueData();
    } catch (error) {
      console.error('Error retrying task:', error);
      showNotification(error.message || 'Error al ejecutar el reintento', 'error');
    } finally {
      setActionLoading(false);
      setActiveTask(null);
    }
  };

  const handleRetryAll = async () => {
    setActionLoading(true);
    try {
      const response = await fetch('/api/routers/sync-queue/retry-all', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al procesar la cola');
      }

      showNotification('Reintento masivo iniciado en segundo plano. Los resultados se actualizarán en breve.', 'info');
      
      // Delay refresh slightly to let background processing start
      setTimeout(fetchQueueData, 2000);
    } catch (error) {
      console.error('Error in retry all:', error);
      showNotification(error.message || 'Error al iniciar reintento masivo', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    setActionLoading(true);
    try {
      const response = await fetch(`/api/routers/sync-queue/${taskToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al eliminar la tarea');
      }

      showNotification('Tarea eliminada de la cola exitosamente', 'success');
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
      fetchQueueData();
    } catch (error) {
      console.error('Error deleting task:', error);
      showNotification(error.message || 'Error al eliminar la tarea', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const getActionChip = (action) => {
    switch (action) {
      case 'add_moroso':
        return <Chip label="Cortar / Suspender" color="warning" variant="outlined" size="small" />;
      case 'remove_moroso':
        return <Chip label="Activar / Reconectar" color="success" variant="outlined" size="small" />;
      default:
        return <Chip label={action} variant="outlined" size="small" />;
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return <Chip icon={<PendingIcon />} label="Pendiente" color="warning" size="small" />;
      case 'processing':
        return <Chip icon={<ProcessingIcon />} label="Procesando" color="info" size="small" />;
      case 'completed':
        return <Chip icon={<SuccessIcon />} label="Completado" color="success" size="small" />;
      case 'failed':
        return <Chip icon={<ErrorIcon />} label="Fallido" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const columns = [
    {
      field: 'created_at',
      headerName: 'Fecha / Hora',
      width: 170,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      }
    },
    { field: 'client_name', headerName: 'Cliente', width: 180 },
    { field: 'ip_address', headerName: 'IP CPE', width: 120 },
    {
      field: 'router_info',
      headerName: 'MikroTik Router',
      width: 180,
      valueGetter: (params) => `${params.row.router_name || 'Desconocido'} (${params.row.router_ip || ''})`
    },
    {
      field: 'action',
      headerName: 'Acción Sinc.',
      width: 160,
      renderCell: (params) => getActionChip(params.value)
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 130,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'attempts',
      headerName: 'Intentos',
      width: 90,
      type: 'number',
      renderCell: (params) => (
        <span className="font-semibold text-gray-700">{params.value}/10</span>
      )
    },
    {
      field: 'last_error',
      headerName: 'Último Error',
      width: 250,
      renderCell: (params) => (
        <Tooltip title={params.value || 'Sin errores registrados'} placement="top">
          <span className="text-xs text-red-600 truncate block max-w-full font-mono bg-red-50 px-1 py-0.5 rounded border border-red-100">
            {params.value || 'N/A'}
          </span>
        </Tooltip>
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const isPendingOrFailed = params.row.status === 'pending' || params.row.status === 'failed';
        const isCurrentActive = activeTask === params.row.id;

        return (
          <Box className="flex items-center space-x-1">
            {isPendingOrFailed && (
              <Tooltip title="Reintentar ahora">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={actionLoading || isCurrentActive}
                    onClick={() => handleRetryTask(params.row.id)}
                  >
                    {isCurrentActive ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SyncIcon size="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Eliminar de la cola">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={actionLoading}
                  onClick={() => {
                    setTaskToDelete(params.row);
                    setDeleteConfirmOpen(true);
                  }}
                >
                  <DeleteIcon size="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  return (
    <Box className="p-6 space-y-6">
      {/* Header */}
      <Box className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <Box>
          <Typography variant="h4" className="font-bold text-gray-800">
            Cola de Sincronización MikroTik
          </Typography>
          <Typography variant="body2" className="text-gray-500 mt-1">
            Administra y reintenta las tareas pendientes de corte y reconexión que fallaron por problemas de conexión con los routers.
          </Typography>
        </Box>
        <Box className="flex space-x-2">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchQueueData}
            disabled={loading || actionLoading}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SyncIcon />}
            onClick={handleRetryAll}
            disabled={loading || actionLoading || (summary.pending === 0 && summary.failed === 0)}
          >
            Reintentar Todos
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="border-l-4 border-yellow-500 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="flex justify-between items-center py-4">
              <Box>
                <Typography variant="body2" className="text-gray-500 font-semibold uppercase tracking-wider">
                  Pendientes
                </Typography>
                <Typography variant="h4" className="font-bold text-yellow-600 mt-1">
                  {loading ? <CircularProgress size={24} /> : summary.pending}
                </Typography>
              </Box>
              <PendingIcon className="text-yellow-500" sx={{ fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className="border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="flex justify-between items-center py-4">
              <Box>
                <Typography variant="body2" className="text-gray-500 font-semibold uppercase tracking-wider">
                  Procesando
                </Typography>
                <Typography variant="h4" className="font-bold text-blue-600 mt-1">
                  {loading ? <CircularProgress size={24} /> : summary.processing}
                </Typography>
              </Box>
              <ProcessingIcon className="text-blue-500" sx={{ fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className="border-l-4 border-red-500 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="flex justify-between items-center py-4">
              <Box>
                <Typography variant="body2" className="text-gray-500 font-semibold uppercase tracking-wider">
                  Fallidos
                </Typography>
                <Typography variant="h4" className="font-bold text-red-600 mt-1">
                  {loading ? <CircularProgress size={24} /> : summary.failed}
                </Typography>
              </Box>
              <ErrorIcon className="text-red-500" sx={{ fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className="border-l-4 border-green-500 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="flex justify-between items-center py-4">
              <Box>
                <Typography variant="body2" className="text-gray-500 font-semibold uppercase tracking-wider">
                  Completados
                </Typography>
                <Typography variant="h4" className="font-bold text-green-600 mt-1">
                  {loading ? <CircularProgress size={24} /> : summary.completed}
                </Typography>
              </Box>
              <SuccessIcon className="text-green-500" sx={{ fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Warning Alert if failed task count is high */}
      {summary.failed > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} className="shadow-sm">
          Hay <strong>{summary.failed}</strong> tareas que han excedido el número máximo de reintentos automáticos y requieren atención. 
          Verifique que los routers MikroTik correspondientes tengan conexión a internet y el puerto API esté abierto, luego haga clic en "Reintentar Todos" o ejecute un reintento manual individual.
        </Alert>
      )}

      {/* Main Table */}
      <Paper className="shadow-sm border border-gray-100 bg-white">
        <DataGrid
          rows={tasks}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          autoHeight
          getRowId={(row) => row.id}
          className="border-none"
          localeText={{
            noRowsLabel: 'No hay tareas de sincronización registradas',
            columnHeaderFiltersLabel: 'Filtros',
            columnMenuSortAsc: 'Ordenar Ascendente',
            columnMenuSortDesc: 'Ordenar Descendente',
            columnMenuFilter: 'Filtrar',
            columnMenuHide: 'Ocultar columna',
            columnMenuShowColumns: 'Mostrar columnas',
            columnMenuUnsort: 'Sin orden',
            MuiTablePagination: {
              labelRowsPerPage: 'Filas por página:',
              labelDisplayedRows: ({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          }}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !actionLoading && setDeleteConfirmOpen(false)}
      >
        <DialogTitle className="flex items-center space-x-2">
          <WarningIcon color="error" />
          <span>Confirmar Eliminación</span>
        </DialogTitle>
        <DialogContent>
          ¿Estás seguro de que deseas eliminar la tarea de sincronización para el cliente <strong>{taskToDelete?.client_name}</strong>?
          <Typography variant="body2" className="text-gray-500 mt-2">
            Eliminar esta tarea significa que el sistema NO intentará sincronizar de forma automática el estado de este cliente con el MikroTik.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteTask}
            color="error"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Eliminar de la Cola'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          className="shadow-md"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RouterSyncStatus;
